'use client';

import {
  FilterBar,
  type TFilterBarPriceRange,
} from '@chemist4u/newt-components';
import { type SortOptions, sortOptions } from '@lib/util/sort-products';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

type ProductToolbarProps = {
  sortBy: SortOptions;
  productCount: number;
  minPrice?: number;
  maxPrice?: number;
  /** True while a client-side sort/filter override is fetching its first
   * page — `productCount` is 0 at that point, so the results label would
   * otherwise misleadingly read "Showing 0 products". */
  isLoading?: boolean;
};

const ProductToolbar = ({
  sortBy,
  productCount,
  minPrice,
  maxPrice,
  isLoading = false,
}: ProductToolbarProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const setQueryParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams);
      for (const [name, value] of Object.entries(updates)) {
        if (value === null) {
          params.delete(name);
        } else {
          params.set(name, value);
        }
      }
      params.set('page', '1');
      router.push(`${pathname}?${params.toString()}`);
    },
    [searchParams, pathname, router],
  );

  const handleSortChange = (value: string) => {
    setQueryParams({ sortBy: value });
  };

  const handlePriceApply = ({ min, max }: TFilterBarPriceRange) => {
    setQueryParams({
      minPrice: min != null ? String(min) : null,
      maxPrice: max != null ? String(max) : null,
    });
  };

  return (
    <div className="relative z-[25] mb-4">
      <FilterBar
        page={1}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={productCount}
        resultsLabel={
          isLoading
            ? 'Loading products…'
            : `Showing ${productCount} ${productCount === 1 ? 'product' : 'products'}`
        }
        sortValue={sortBy}
        sortOptions={sortOptions}
        onSortChange={handleSortChange}
        priceMin={minPrice ?? null}
        priceMax={maxPrice ?? null}
        onPriceApply={handlePriceApply}
      />
    </div>
  );
};

export default ProductToolbar;
